import { useEffect, useMemo, useState } from 'react';
import { onAuthStateChanged, signInWithEmailAndPassword, signOut } from 'firebase/auth';
import { collection, getDocs, orderBy, query } from 'firebase/firestore';
import { auth, db } from '../firebase';

const formatDate = (timestampValue) => {
  if (!timestampValue || typeof timestampValue.toDate !== 'function') {
    return 'Date not available';
  }

  return timestampValue.toDate().toLocaleString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const Dashboard = () => {
  const [credentials, setCredentials] = useState({ email: '', password: '' });
  const [isSigningIn, setIsSigningIn] = useState(false);
  const [authError, setAuthError] = useState('');

  const [currentUser, setCurrentUser] = useState(null);
  const [isAuthLoading, setIsAuthLoading] = useState(true);

  const [contactForms, setContactForms] = useState([]);
  const [isFormsLoading, setIsFormsLoading] = useState(false);
  const [formsError, setFormsError] = useState('');

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setCurrentUser(user);
      setIsAuthLoading(false);
    });

    return unsubscribe;
  }, []);

  useEffect(() => {
    const loadForms = async () => {
      if (!currentUser) {
        setContactForms([]);
        return;
      }

      setIsFormsLoading(true);
      setFormsError('');

      try {
        const contactQuery = query(collection(db, 'contact_us'), orderBy('createdAt', 'desc'));
        const snapshot = await getDocs(contactQuery);
        const forms = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
        setContactForms(forms);
      } catch (error) {
        setFormsError('Unable to load contact form submissions right now.');
      } finally {
        setIsFormsLoading(false);
      }
    };

    loadForms();
  }, [currentUser]);

  const headingText = useMemo(() => {
    if (isAuthLoading) {
      return 'Loading dashboard...';
    }

    return currentUser ? 'Dashboard' : 'Dashboard Login';
  }, [currentUser, isAuthLoading]);

  const handleCredentialChange = (event) => {
    const { name, value } = event.target;
    setCredentials((prev) => ({ ...prev, [name]: value }));
  };

  const handleSignIn = async (event) => {
    event.preventDefault();

    setIsSigningIn(true);
    setAuthError('');

    try {
      await signInWithEmailAndPassword(auth, credentials.email, credentials.password);
      setCredentials({ email: '', password: '' });
    } catch (error) {
      setAuthError('Invalid email or password. Please try again.');
    } finally {
      setIsSigningIn(false);
    }
  };

  const handleLogout = async () => {
    try {
      await signOut(auth);
    } catch (error) {
      window.alert('Unable to sign out right now. Please try again.');
    }
  };

  return (
    <>
      <section className="page-hero">
        <h1>{headingText}</h1>
      </section>

      <section className="dashboard-section">
        <div className="dashboard-container">
          {isAuthLoading ? (
            <div className="dashboard-card">
              <p>Checking authentication status...</p>
            </div>
          ) : null}

          {!isAuthLoading && !currentUser ? (
            <div className="dashboard-card dashboard-login-card">
              <h2>Sign in with Email and Password</h2>
              <p className="dashboard-subtext">Use your Firebase account credentials to access Contact Us submissions.</p>

              <form className="dashboard-login-form" onSubmit={handleSignIn}>
                <label htmlFor="dashboard-email">Email</label>
                <input
                  id="dashboard-email"
                  type="email"
                  name="email"
                  value={credentials.email}
                  onChange={handleCredentialChange}
                  required
                  placeholder="name@company.com"
                />

                <label htmlFor="dashboard-password">Password</label>
                <input
                  id="dashboard-password"
                  type="password"
                  name="password"
                  value={credentials.password}
                  onChange={handleCredentialChange}
                  required
                  placeholder="••••••••"
                />

                {authError ? <p className="dashboard-error">{authError}</p> : null}

                <button type="submit" className="submit-btn" disabled={isSigningIn}>
                  {isSigningIn ? 'Signing in...' : 'Sign in'}
                </button>
              </form>
            </div>
          ) : null}

          {!isAuthLoading && currentUser ? (
            <div className="dashboard-card">
              <div className="dashboard-header-row">
                <div>
                  <h2>Contact Us Submissions</h2>
                  <p className="dashboard-subtext">Signed in as {currentUser.email}</p>
                </div>
                <button type="button" className="dashboard-logout-btn" onClick={handleLogout}>
                  Sign out
                </button>
              </div>

              {isFormsLoading ? <p>Loading submissions...</p> : null}
              {formsError ? <p className="dashboard-error">{formsError}</p> : null}

              {!isFormsLoading && !formsError ? (
                contactForms.length ? (
                  <div className="dashboard-table-wrap">
                    <table className="dashboard-table">
                      <thead>
                        <tr>
                          <th>Name</th>
                          <th>Phone</th>
                          <th>Email</th>
                          <th>Service</th>
                          <th>Message</th>
                          <th>Received At</th>
                        </tr>
                      </thead>
                      <tbody>
                        {contactForms.map((form) => (
                          <tr key={form.id}>
                            <td>{form.name || '-'}</td>
                            <td>{form.phone || '-'}</td>
                            <td>{form.email || '-'}</td>
                            <td>{form.service || '-'}</td>
                            <td>{form.message || '-'}</td>
                            <td>{formatDate(form.createdAt)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p>No Contact Us forms received yet.</p>
                )
              ) : null}
            </div>
          ) : null}
        </div>
      </section>
    </>
  );
};

export default Dashboard;
